/**
 * React hooks for reading/writing typed URL search params.
 *
 * Schemas, types, and pure utilities live in @atlas-int/data (canonical source).
 * This file provides the React Router integration layer.
 */

import { useMemo, useCallback } from "react";
import { useSearchParams } from "react-router";
import { VizType } from "@atlas-int/data/types";
import { useCurrentVizType } from "../routing/typedRoutes";

// Re-export everything from the data package for backward compatibility
export {
  // Schemas
  treemapParamSchema,
  geoParamSchema,
  overtimeParamSchema,
  marketshareParamSchema,
  feasibilityParamSchema,
  productspaceParamSchema,
  vizParamSchemas,
  // Types
  type BaseExploreParams,
  type TreeParams,
  type GeoParams,
  type OverTimeParams,
  type MarketShareParams,
  type FeasibilityParams,
  type ProductSpaceParams,
  type VizParamMap,
  type DecodedExploreParams,
  type ExploreParamKey,
  // Utilities
  parseSearchParams,
  getDefaults,
  serializeParams,
  resetParamsPerViz,
  primarySelections,
  mergeParams,
} from "@atlas-int/data";

import type { VizParamMap, BaseExploreParams, DecodedExploreParams } from "@atlas-int/data";
import { parseSearchParams, serializeParams } from "@atlas-int/data";

// ── React hooks ───────────────────────────────────────────────────

/** Setter type for typed explore params. */
export type SetExploreParams<V extends VizType> = (
  updates: Partial<VizParamMap[V]>,
) => void;

/**
 * Read and write typed, validated URL search params for a given viz type.
 *
 * Parses the current URL with the viz-specific Zod schema, returning
 * narrowed types. The setter merges partial updates, serializes back
 * to the URL, and omits default values automatically.
 */
export function useTypedExploreParams<V extends VizType>(
  vizType: V,
): [VizParamMap[V], SetExploreParams<V>] {
  const [searchParams, setSearchParams] = useSearchParams();

  const parsed = useMemo(
    () => parseSearchParams(vizType, searchParams),
    [vizType, searchParams],
  );

  const setParams: SetExploreParams<V> = useCallback(
    (updates) => {
      setSearchParams((prev) => {
        const current = parseSearchParams(vizType, prev);
        const merged = { ...current, ...updates } as VizParamMap[V];
        return serializeParams(vizType, merged);
      });
    },
    [vizType, setSearchParams],
  );

  return [parsed, setParams];
}

// ── Per-viz convenience hooks ─────────────────────────────────────

export function useTreemapParams() {
  return useTypedExploreParams(VizType.Tree);
}
export function useGeomapParams() {
  return useTypedExploreParams(VizType.Geo);
}
export function useOvertimeParams() {
  return useTypedExploreParams(VizType.OverTime);
}
export function useMarketshareParams() {
  return useTypedExploreParams(VizType.MarketShare);
}
export function useFeasibilityParams() {
  return useTypedExploreParams(VizType.Feasibility);
}
export function useProductspaceParams() {
  return useTypedExploreParams(VizType.ProductSpace);
}

/**
 * Read and write the base params that are shared across ALL viz types.
 */
export function useBaseExploreParams(): [BaseExploreParams, (updates: Partial<BaseExploreParams>) => void] {
  const vizType = useCurrentVizType();
  const [params, setParams] = useTypedExploreParams(vizType);
  // Extract only base keys — the setter passes through to the full schema
  const baseParams = useMemo(() => {
    const { exporter, importer, view, year, startYear, endYear, tradeDirection, productClass, product } = params as BaseExploreParams & Record<string, unknown>;
    return { exporter, importer, view, year, startYear, endYear, tradeDirection, productClass, product };
  }, [params]);
  return [baseParams, setParams as (updates: Partial<BaseExploreParams>) => void];
}

/**
 * Hook for cross-viz components that need access to viz-specific params
 * without knowing the current viz type at compile time.
 */
export function useExploreParams(): [DecodedExploreParams, (updates: Partial<DecodedExploreParams>) => void] {
  const vizType = useCurrentVizType();
  return useTypedExploreParams(vizType) as [DecodedExploreParams, (updates: Partial<DecodedExploreParams>) => void];
}
